/* eslint-disable @typescript-eslint/no-explicit-any */
import {
	redirect,
	type LoaderFunctionArgs,
	type MetaFunction,
	type ActionFunctionArgs,
	Link,
	type Location,
	useLocation,
	useNavigate,
	useLoaderData,
	data,
	type HeadersArgs,
} from 'react-router'
import { isbot } from 'isbot'
import mapPlaceholder from '/img/map-placeholder.jpg'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getStructuredDataStatus } from './events.helper'
import BookingButton from '~/components/booking-button/booking-button'
import { Button } from '~/components/button/button'
import { LinkButton } from '~/components/button/link-button'
import Chip from '~/components/chip/Chip'
import Container from '~/components/container/container'
import EventOrganiserBlock from '~/components/event-organizer-block/event-organizer-block'
import EventOwnershipRequestBlock from '~/components/event-ownership-request-block/event-ownership-request-block'
import EventQaaModal from '~/components/event-qaa-modal/event-qaa-modal'
import EventReportBlock from '~/components/event-report-block/event-report-block'
import { FavoriteEvent } from '~/components/favorite-event/favorite-event'
import { getIconName } from '~/components/icon/icon.helper'
import { Icon } from '~/components/icon/icon'
import LoginModal from '~/components/login-modal/login-modal'
import MapStatic from '~/components/map-static/map-static'
import RelatedEvent from '~/components/related-event/related-event'
import ShareEventModal from '~/components/share-event-modal/share-event-modal'
import { Spacer } from '~/components/spacer/spacer'
import SubscribeToNewsletter from '~/components/subscribe-to-newsletter/subscribe-to-newsletter'
import Text from '~/components/text/text'
import WarningBlock from '~/components/warning-block/warning-block'
import { getBaseEvent } from '~/queries/getBaseEvents.server'
import { getRelatedEvents } from '~/queries/getRelatedEvents.server'
import { putStatistics_View } from '~/queries/statistics/putStatistics.view.server'
import {
	getLanguageFromCountryCode,
	getServerLang,
	useClientLang,
} from '~/utils/i18n.utils'
import { getImgSrc } from '~/utils/image'
import { invariantResponse } from '~/utils/misc'
import { SetMeta, formatDescription } from '~/utils/seo/seo'
import { getEventUrl } from '~/utils/seo/seo-misc'
import { trackClick } from '~/utils/statistics/statistics.helper'
import { makeTimings, time } from '~/utils/timing.server'
import { useIsAdmin, useOptionalUser } from '~/utils/user'
import { requestOwnerChangeAction } from './request-owner-change.server'
import { reportEventAction } from '~/components/event-report-block/report-event.server'
import { getUserId } from '~/utils/auth/session.server'
import { getPromotedIndexEvents } from '~/queries/indexEvents/getPromotedIndexEvents.server'
import Calendar from '~/components/calendar/calendar'
import UserBanner from '~/components/user-banner/user-banner'
import { format } from 'date-fns'
import { getBaseEvent301 } from '~/queries/getBaseEvents.301.server'

export async function loader({ request, params }: LoaderFunctionArgs) {
	const timings = makeTimings('event loader')
	const userId = await getUserId(request)
	const lng = getServerLang(request)

	let event = await time(
		() =>
			getBaseEvent({
				eventId: params.eventId,
				lng,
				dateParam: params.date,
				titleParam: params.title,
				userId: userId ?? undefined,
			}),
		{ timings, type: 'get event' },
	)
	// If the event is not found, check if it's a redirect to the old structure
	if(!event) {
		const redirectEvent = await time(
			() =>
				getBaseEvent301({
					dateId: params.eventId,
					lng,
				}),
			{ timings, type: 'get event by dateId (old structure)' },
		)
		if (redirectEvent) {
			return redirect(redirectEvent.redirectTo, {
				status: redirectEvent.status,
			})
		}
	}
	if (!event) {
		const isBot = isbot(request.headers.get('user-agent'))
		const promotedEvents = await time(
			async () =>
				await getPromotedIndexEvents({
					fromDate: new Date(),
					take: 4,
					isBot: isBot,
					lng: lng,
				}),
			{ timings, type: 'get promoted events' },
		)
		throw data(
			{ promotedEvents, message: 'event' },
			{
				headers: { 'Server-Timing': timings.toString() },
				status: 404,
			},
		)
	}
	invariantResponse(event, 'event', { status: 404 })

	// Redirect to correct url if the url is incorrect for SEO purposes
	if ((params.date && !event.urlDateMatch) || !event.urlTitleMatch) {
		return redirect(
			getEventUrl({
				eventId: event.id,
				title: event.title,
				timeZone: event.timeZone,
				lng: lng.lngShort,
				datetime: event.urlDateMatch ? event.selectedDate.dateFrom : undefined,
			}),
			{
				status: 301,
			},
		)
	}

	const relatedEvents = await time(
		() => getRelatedEvents(event.title, event.category.id, event.city!, lng),
		{ timings, type: 'get related events' },
	)

	if (!isbot(request.headers.get('user-agent'))) {
		putStatistics_View(event.selectedDate.id, userId)
	}

	return data(
		{ event, relatedEvents },
		{ headers: { 'Server-Timing': timings.toString() } },
	)
}

export function headers({ parentHeaders, loaderHeaders }: HeadersArgs) {
	parentHeaders.append('Server-Timing', loaderHeaders.get('Server-Timing')!)
	return parentHeaders
}

export async function action(args: ActionFunctionArgs) {
	const { request } = args
	// Clone the request before reading FormData
	const clonedRequest = request.clone()
	const formData = await clonedRequest.formData()
	const formType = formData.get('formType')

	switch (formType) {
		case 'report':
			return reportEventAction({ ...args, request })
		case 'change':
			return requestOwnerChangeAction({ ...args, request })
		// case 'feedback':
		// 	return feedbackEventAction({ ...args, request })  // Pass original request
		default:
			throw new Error('Invalid form type')
	}
}

export default function SpecificEventPage() {
	const { event, relatedEvents } = useLoaderData<typeof loader>()
	const { t } = useTranslation()
	const lng = useClientLang()
	const navigate = useNavigate()
	const location = useLocation()
	const { state } = location
	const user = useOptionalUser()
	const isAdmin = useIsAdmin()
	const isEventOrganizer = user?.id === event.owner.id
	const [showLoginModal, setShowLoginModal] = useState(false)
	const [showMap, setShowMap] = useState(false)
	const [showShareModal, setShowShareModal] = useState(false)
	const [showRestrictionModal, setShowRestrictionModal] = useState(false)
	const toggleShareModal = () => setShowShareModal(prev => !prev)
	const toggleLoginModal = () => setShowLoginModal(prev => !prev)
	const toggleRestrictionModal = () => setShowRestrictionModal(prev => !prev)

	const goBack = () => {
		if (state?.from === 'internal') {
			navigate(-1)
		} else {
			navigate('/')
		}
	}
	const toggleMap = () => setShowMap(prev => !prev)
	const getResellerLogo = (url?: string) => {
		if (!url) return undefined
		if (url.includes('tickster')) {
			return {
				logo: '/img/tickster_logo.webp',
				height: 'h-5',
				resellerName: 'Tickster',
			}
		} else if (url.includes('ticketmaster')) {
			return {
				logo: '/img/ticketmaster_logo.png',
				height: 'h-5',
				resellerName: 'Ticketmaster',
			}
		} else if (url.includes('billetto')) {
			return {
				logo: '/img/billetto_logo.png',
				height: 'h-8',
				resellerName: 'Billetto',
			}
		} else if (url.includes('fever')) {
			return {
				logo: '/img/fever_logo.png',
				height: 'h-6',
				resellerName: 'Fever',
			}
		} else {
			return undefined
		}
	}

	const resellerLogo = getResellerLogo(event.bookingLink)
	return (
		<div
			className="relative bg-cover bg-center bg-no-repeat"
			style={{
				position: 'relative',
			}}
		>
			<div
				className="absolute inset-0 -z-10 bg-cover bg-center bg-no-repeat opacity-10 blur-[50px]"
				style={{
					backgroundImage: `url(${getImgSrc(event?.imageId, 'event')})`,
					backgroundPosition: 'center',
					backgroundSize: 'cover',
				}}
			/>
			{showShareModal && <ShareEventModal toggleModal={toggleShareModal} />}
			{showLoginModal && (
				<LoginModal
					text={t('misc_login_to_add_favorite')}
					initialPage={0}
					toggleLoginModal={toggleLoginModal}
				/>
			)}
			{showRestrictionModal && (
				<EventQaaModal
					bookingUrl={event.bookingLink}
					toggleModal={toggleRestrictionModal}
					country={event.country}
				/>
			)}
			<Container
				marginY={false}
				className="my-0! max-w-7xl px-0! pt-0 pb-4 md:px-10! md:pt-20 md:pb-6 lg:px-14!"
			>
				<div className="grid grid-cols-2 gap-y-4 md:gap-x-4">
					<div className="col-span-2">
						<div
							className="relative h-[250px] w-full md:h-[400px]"
							style={{
								viewTransitionName: `img-${event.selectedDate.id}`,
							}}
						>
							<Button
								className="border-border! bg-card/80! text-card-foreground! hover:bg-card/90! absolute top-3 left-3 h-11! w-11! rounded-full! border! p-2! md:h-10! md:w-10!"
								onClick={goBack}
							>
								<Icon name="arrow-left" size="lg" />
							</Button>
							<img
								src={getImgSrc(event?.imageId, 'event')}
								alt={event?.title}
								className="h-full w-full object-cover md:rounded-xl"
							/>
							{event && (
								<Chip
									className="absolute bottom-10 left-2 md:bottom-8"
									text={
										<div className="flex items-center">
											<Icon
												name={getIconName(event.category.icon)}
												size="xs"
												className="mr-1"
											/>
											{event.category.name}
										</div>
									}
									color={event.category.color}
								/>
							)}
							<div className="absolute top-3 right-3">
								<div className="flex items-center justify-end gap-2">
									{user ? (
										<FavoriteEvent
											favoredByUser={user?.favorites.some(
												e => e.id === event.selectedDate.id,
											)}
											eventDateId={event.selectedDate.id}
											eventName={event?.title}
										/>
									) : (
										<div className="z-1 transition duration-200 hover:scale-105">
											<button
												aria-label={t('action_save_favorite')}
												title={t('action_save_favorite')}
												className="text-foreground/40 dark:text-background/50 cursor-pointer items-center justify-center px-4 py-2"
												onClick={toggleLoginModal}
											>
												<Icon
													className="stoke-1 scale-125 stroke-white max-md:scale-150"
													name="heart-2"
													size="lg"
												/>
											</button>
										</div>
									)}
									<div
										className="border-border bg-card/80 text-card-foreground hover:bg-card/90 flex h-11 w-11 cursor-pointer items-center justify-center rounded-full border duration-100 md:h-10! md:w-10!"
										onClick={toggleShareModal}
										title={t('action_share_event')}
									>
										<Icon name="share-2" size="md" />
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</Container>
			<Container marginY={false} className="!lg:px-0 mb-8">
				<div className="grid grid-cols-12 gap-y-4 md:gap-x-4 lg:gap-x-12">
					<section className="col-span-12 space-y-1 md:col-span-6 lg:col-span-8">
						<div className="mb-4 flex items-center justify-between">
							<Text
								as="h1"
								className="font-semibold"
								viewTransitionName={`ttl-${event.selectedDate.id}`}
							>
								{event?.title}
							</Text>
							{event.ownerAsOrganizer && (
								<>
									{event.owner.isAdmin ? (
										<>
											<img
												src={getImgSrc(event?.owner.imageId, 'user')}
												alt={event.owner.name ?? ''}
												className="h-12 w-12 rounded-full object-cover"
											/>
										</>
									) : (
										<Link
											to={`/users/${event.owner.id}`}
											className="h-12 w-12 shrink-0"
										>
											<img
												src={getImgSrc(event?.owner.imageId, 'user')}
												alt={event?.owner.name ?? ''}
												className="h-12 w-12 rounded-full object-cover"
											/>
										</Link>
									)}
								</>
							)}
						</div>
						<hr />
						<br />
						{event.state === 'Cancelled' ? (
							<>
								<WarningBlock text={t('events_cancelled_warning')} />
								<br />
							</>
						) : event.eventHasPassed ? (
							<>
								<WarningBlock text={t('events_old_event_warning')} />
								<br />
							</>
						) : event.state === 'SoldOut' ? (
							<>
								<WarningBlock text={t('events_sold_out_warning')} />
								<br />
							</>
						) : (
							event.state === 'NotReleased' && (
								<>
									<WarningBlock text={t('events_not_released_warning')} />
									<br />
								</>
							)
						)}

						{event?.formattedAddress && (
							<Text className="flex items-center py-2">
								<Icon name="position-marker" className="mr-2 text-2xl" />
								<a
									href={
										event?.addressUrl ??
										`https://maps.google.com/?q=${event?.formattedAddress}`
									}
									target="_blank"
									rel="noreferrer"
									className="text-secondary underline"
								>
									{event?.formattedAddress}
								</a>
							</Text>
						)}

						{event.selectedDate.id && (
							<h2 className="text-body-md! flex items-center py-2">
								<Icon name="calendar" className="mr-2 text-xl" />
								<time dateTime={event.selectedDate.dateFrom.toISOString()}>
									{event.locationString}
								</time>
								{event.selectedDate.newDate && (
									<Chip
										text={t('form_label_new_date')}
										color="indigo"
										className="ml-2"
									/>
								)}
							</h2>
						)}

						{event.price >= 0 && (
							<Text className="flex items-center py-2">
								<Icon name="currency-dollar" className="mr-2 text-xl" />
								{event.price === 0
									? t('price_free')
									: event.currency &&
										new Intl.NumberFormat(lng.lngLong, {
											style: 'currency',
											currency: event.currency,
											minimumFractionDigits: 0,
										}).format(event.price)}
							</Text>
						)}
						{resellerLogo && (
							<div className="flex justify-end">
								<Link
									to={event.bookingLink}
									target="_blank"
									onClick={() => trackClick(event.selectedDate.id, 'clicks')}
								>
									<img
										src={resellerLogo.logo}
										alt={t('action_book_ticket_at', {
											reseller: resellerLogo.resellerName,
										})}
										className={`"object-fit ${resellerLogo.height} invert dark:invert-0`}
										loading="lazy"
									/>
								</Link>
							</div>
						)}
						<br />
						<hr />
						<Text as="h3" className="text-h3! pt-5">
							{t('form_label_description')}
						</Text>
						{event?.content && event.content.length > 0 ? (
							<div
								className="break-word"
								dangerouslySetInnerHTML={{ __html: event?.content }}
							/>
						) : (
							<Text>{t('form_label_no_description')}</Text>
						)}
					</section>
					<section className="border-border bg-card col-span-12 flex flex-col gap-y-4 rounded-xl border shadow-xs md:col-span-6 lg:col-span-4">
						<Calendar
							dates={event.dates}
							selectedDate={event.selectedDate}
							timeZone={event.timeZone}
						/>
						<div className="p-4 max-md:hidden">
							{event?.bookingLink && (
								<BookingButton
									eventDateId={event.selectedDate.id}
									bookingLink={event.bookingLink}
									state={event.state}
								/>
							)}
							{(isAdmin || isEventOrganizer) && (
								<LinkButton
									size="lg"
									className="mt-2 w-full"
									to={
										isAdmin
											? `/admin/events/${event.id}`
											: `/my-events/${event.id}/edit`
									}
								>
									{t('action_edit')}
								</LinkButton>
							)}
							<Button
								onClick={toggleRestrictionModal}
								className="mt-2 w-full"
								variant="primary"
							>
								{t('terms_and_conditions_q_and_a')}
							</Button>
						</div>
						<div>
							<Spacer size="2xs" />
							<EventOwnershipRequestBlock event={event} />
							<EventReportBlock event={event} />
						</div>
					</section>
					<section className="col-span-12 my-4">
						<UserBanner />
					</section>
					<section className="col-span-12 my-4">
						<div className="h-[250px] w-full md:h-[400px]">
							{showMap ? (
								<MapStatic lng={event.longitude} lat={event.latitude} />
							) : (
								<div className="text-foreground relative h-[250px] w-full overflow-hidden rounded-xl shadow-xs md:h-[400px]">
									<img
										className="h-full w-full rounded-xl object-cover opacity-80 dark:opacity-50"
										src={mapPlaceholder}
									/>
									<Button
										onClick={toggleMap}
										variant="outline"
										className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 transform"
									>
										{t('action_show_on_map')}
									</Button>
								</div>
							)}
						</div>
					</section>
					<EventOrganiserBlock event={event} />
					{relatedEvents.length > 0 && (
						<div className="col-span-12 mt-8 mb-8 md:mb-10">
							<Text as="h3">{t('events_related_events')}</Text>
							<div className="col-span-12 grid grid-cols-12 gap-4">
								{relatedEvents.map(e => (
									<RelatedEvent key={e.dateId} event={e} />
								))}
							</div>
						</div>
					)}
					{(!user || !user.newsletterSubscriber) && <SubscribeToNewsletter />}
				</div>
			</Container>
			{event?.bookingLink && (
				<div className="border-border bg-background text-foreground fixed bottom-0 z-20 w-full border-t p-4 shadow-xs md:hidden">
					<div className="flex gap-4">
						<BookingButton
							eventDateId={event.selectedDate.id}
							bookingLink={event.bookingLink}
							state={event.state}
						/>
					</div>
				</div>
			)}
		</div>
	)
}

export const meta: MetaFunction = ({
	data,
	location,
}: {
	data: any
	location: Location
}) => {
	const event = data.event
	const title = event.title
	const organizer =
		!event.owner.isAdmin && event.ownerAsOrganizer
			? event.owner.name
			: event.organizerName
	const status = getStructuredDataStatus(event.state)
	const structuredData = {
		'script:ld+json': {
			'@context': 'https://schema.org',
			'@graph': [
				{
					'@type': 'Event',
					name: event.title,
					...(event.selectedDate.dateFrom && {
						startDate: format(event.selectedDate.dateFrom, 'yyyy-MM-dd'),
					}),
					...(event.selectedDate.dateTo && {
						endDate: format(event.selectedDate.dateTo, 'yyyy-MM-dd'),
					}),
					eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
					...(status && { eventStatus: status }),
					location: {
						'@type': 'Place',
						...(event.venueName && { name: event.venueName }),
						address: {
							'@type': 'PostalAddress',
							...(event.street && { streetAddress: event.street }),
							...(event.city && { addressLocality: event.city }),
							...(event.postalCode && { postalCode: event.postalCode }),
							...(event.region && { addressRegion: event.region }),
							...(event.country && { addressCountry: event.country }),
						},
					},

					image: [`https://evently.se${getImgSrc(event.imageId)}`],
					description: formatDescription(event.content, 200),
					...(event.organizerName && {
						organizer: {
							'@type': 'Organization',
							name: event.organizerName,
						},
					}),

					...(event.price && {
						offers: {
							'@type': 'Offer',
							price: event.price,
							priceCurrency: 'SEK',
						},
					}),
				},
			],
		},
	}
	return SetMeta({
		title: !organizer ? title : `${title} - ${organizer}`,
		description: data?.event?.content ?? '',
		location,
		image: getImgSrc(data?.event?.imageId, 'event'),
		robots: data.event.eventHasPassed ? 'noindex' : 'all',
		structuredData: event ? structuredData : undefined,
		canonical: getLanguageFromCountryCode(data?.event?.countryCode),
		country: data?.event?.countryCode,
	})
}
